import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { accountRequestGate } from "./account-request-gate";
type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
export type SelectedPlan = {
  planId: string;
  planVersion: number;
  quantity: number;
};
type Plan = {
  id: string;
  name: string;
  unit: string;
  version: number;
  catalogDraft: boolean;
  priceConfigured: boolean;
  catalogUnitMinor: number | null;
};
const dollars = (minor: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    minor / 100,
  );

export function PlanChoice({
  api,
  kind,
  value,
  onChange,
  disabled = false,
  complimentary = false,
}: {
  api: Api;
  kind: string;
  value: SelectedPlan | null;
  onChange: (value: SelectedPlan | null) => void;
  disabled?: boolean;
  complimentary?: boolean;
}) {
  const [options, setOptions] = useState<Plan[] | null>(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setOptions(null);
    setError("");
    void api<{ items: Plan[] }>(
      "/admin/plans/selection-options?kind=" + encodeURIComponent(kind),
      { signal: controller.signal },
    )
      .then((result) => {
        if (!controller.signal.aborted) setOptions(result.items);
      })
      .catch((cause) => {
        if (!controller.signal.aborted) setError(cause.message);
      });
    return () => controller.abort();
  }, [api, kind, revision]);
  const selected = options?.find((plan) => plan.id === value?.planId);
  return (
    <>
      <label className="cr-field">
        <span>Plan</span>
        <select
          value={value?.planId ?? ""}
          disabled={disabled || !options}
          onChange={(event) => {
            const plan = options?.find(
              (item) => item.id === event.target.value,
            );
            onChange(
              plan
                ? { planId: plan.id, planVersion: plan.version, quantity: 1 }
                : null,
            );
          }}
        >
          <option value="">
            {options ? "Choose later" : "Loading plans…"}
          </option>
          {options?.map((plan) => (
            <option key={plan.id} value={plan.id}>
              {plan.name}
              {plan.catalogDraft ? " · Draft" : ""} ·{" "}
              {complimentary
                ? "Complimentary"
                : plan.catalogUnitMinor === null
                  ? "Price not set"
                  : dollars(plan.catalogUnitMinor) +
                    (plan.unit === "seat" ? " per user / month" : " / month")}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        className="cr-text-button"
        disabled={disabled}
        onClick={() => {
          onChange(null);
          setRevision((value) => value + 1);
        }}
      >
        Refresh plan choices
      </button>
      {error && (
        <p className="cr-notice error" role="alert">
          {error}{" "}
          <button
            type="button"
            className="cr-text-button"
            disabled={disabled}
            onClick={() => setRevision((value) => value + 1)}
          >
            Reload plans
          </button>
        </p>
      )}
      {options?.length === 0 && (
        <p>
          No{" "}
          {kind === "enterprise"
            ? "enterprise"
            : kind === "residential"
              ? "residential"
              : "commercial"}{" "}
          plans have been added. You can choose one later.
        </p>
      )}
      {selected?.unit === "seat" && (
        <label className="cr-field">
          <span>Number of users</span>
          <input
            type="number"
            min={1}
            max={10000}
            step={1}
            required
            disabled={disabled}
            value={Number.isFinite(value?.quantity) ? value!.quantity : ""}
            onChange={(event) =>
              onChange({
                planId: selected.id,
                planVersion: selected.version,
                quantity: event.target.valueAsNumber,
              })
            }
          />
        </label>
      )}
      <p className="cr-form-note">
        This records a plan for setup. It does not activate service or start
        billing.
        {selected?.catalogDraft ? " Draft terms still need staff review." : ""}
      </p>
    </>
  );
}

type SelectionState = {
  version: number;
  account: { version: number; kind: string; billingMode: string };
  selection: null | {
    name: string;
    unit: string;
    quantity: number;
    catalogDraft: boolean;
    reviewRequired: boolean;
    pricingState: string;
    proposedRecurringMinor: number | null;
  };
};
export function AccountPlanPanel({
  api,
  csrf,
  accountId,
  customer = false,
  readOnly = false,
  refreshVersion,
}: {
  api: Api;
  csrf: string | undefined;
  accountId: string;
  customer?: boolean;
  readOnly?: boolean;
  refreshVersion: number;
}) {
  const base =
    "/" +
    (customer ? "customer" : "admin") +
    "/accounts/" +
    accountId +
    "/plan-selection";
  const [state, setState] = useState<SelectionState | null>(null);
  const [choice, setChoice] = useState<SelectedPlan | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const controller = useRef<AbortController | null>(null);
  const gate = useRef(accountRequestGate()).current;
  const receipt = useRef({ hash: "", key: crypto.randomUUID() });
  const load = useCallback(async () => {
    const signal = controller.current?.signal;
    const current = gate.open(accountId);
    setBusy(true);
    try {
      const result = await api<SelectionState>(base, signal ? { signal } : {});
      if (!signal?.aborted && current()) {
        setState(result);
        setError("");
        return true;
      }
      return false;
    } catch (cause) {
      if (!signal?.aborted && current()) setError((cause as Error).message);
      return false;
    } finally {
      if (!signal?.aborted && current()) setBusy(false);
    }
  }, [api, base, accountId, gate]);
  useEffect(() => {
    const life = new AbortController();
    controller.current = life;
    setBusy(false);
    void load();
    return () => {
      life.abort();
      gate.invalidate();
    };
  }, [load, refreshVersion, gate]);
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!state || customer || readOnly || busy) return;
    if (
      !choice &&
      (!state.selection ||
        new FormData(event.currentTarget).get("confirmClear") !== "on")
    )
      return;
    const input = {
      expectedVersion: state.version,
      expectedAccountVersion: state.account.version,
      selection: choice,
    };
    const hash = JSON.stringify(input);
    if (hash !== receipt.current.hash)
      receipt.current = { hash, key: crypto.randomUUID() };
    setBusy(true);
    setError("");
    setNotice("");
    const signal = controller.current?.signal;
    const current = gate.open(accountId);
    try {
      const saved = await api<SelectionState>(base, {
        method: "POST",
        csrf,
        body: { ...input, requestKey: receipt.current.key },
        ...(signal ? { signal } : {}),
      });
      if (!signal?.aborted && current()) {
        setState(saved);
        setChoice(null);
        setNotice(
          saved.selection
            ? "Plan selected for setup. Service and billing are unchanged."
            : "Pending plan cleared. Service and billing are unchanged.",
        );
      }
    } catch (cause) {
      if (!signal?.aborted && current()) setError((cause as Error).message);
    } finally {
      if (!signal?.aborted && current()) setBusy(false);
    }
  }
  return (
    <section className="cr-panel">
      <h3>Plan</h3>
      {error && (
        <p className="cr-notice error" role="alert">
          {error}{" "}
          <button
            className="cr-text-button"
            disabled={busy}
            onClick={() =>
              void load().then((accepted) => {
                if (accepted) {
                  setError("");
                  setChoice(null);
                }
              })
            }
          >
            Reload plan
          </button>
        </p>
      )}
      {notice && (
        <p className="cr-notice" role="status">
          {notice}
        </p>
      )}
      {!state ? (
        !error && <p role="status">Loading selected plan…</p>
      ) : (
        <>
          {state.selection ? (
            <>
              <p>
                <strong>{state.selection.name}</strong>
                {state.selection.unit === "seat"
                  ? " · " + state.selection.quantity + " users"
                  : ""}
              </p>
              <p>
                Pending plan selection. Existing subscriptions are unchanged.
              </p>
              <p>
                {state.selection.pricingState === "complimentary"
                  ? "Complimentary service · $0.00"
                  : state.selection.proposedRecurringMinor === null
                    ? "Price needs staff review."
                    : "Proposed monthly charge: " +
                      dollars(state.selection.proposedRecurringMinor)}
              </p>
              {state.selection.reviewRequired && (
                <p className="cr-notice">
                  The account or plan terms changed. Staff must review this
                  selection before activation.
                </p>
              )}
            </>
          ) : (
            <p>
              No plan selected yet.
              {state.account.billingMode === "complimentary"
                ? " This account has complimentary service."
                : ""}
            </p>
          )}
          {!customer && !readOnly && (
            <details className="cr-editor">
              <summary>
                {state.selection ? "Change selected plan" : "Select a plan"}
              </summary>
              <form className="cr-form" onSubmit={save}>
                <PlanChoice
                  api={api}
                  kind={state.account.kind}
                  value={choice}
                  onChange={setChoice}
                  disabled={busy}
                  complimentary={state.account.billingMode === "complimentary"}
                />
                {!choice && state.selection && (
                  <label className="cr-check">
                    <input
                      type="checkbox"
                      name="confirmClear"
                      required
                      disabled={busy}
                    />
                    <span>
                      Clear this pending selection. Active service and billing
                      will not change.
                    </span>
                  </label>
                )}
                <button
                  className="cr-button"
                  disabled={busy || (!choice && !state.selection)}
                >
                  {busy
                    ? "Saving plan…"
                    : choice
                      ? "Save selected plan"
                      : "Clear pending plan"}
                </button>
              </form>
            </details>
          )}
        </>
      )}
    </section>
  );
}
